import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WHOLESALE_METHOD_LABELS, WHOLESALE_PAYMENT_METHODS, type WholesalePaymentMethod, type WholesalePosOrderDTO } from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input, Select, Skeleton, useToast } from '@bifeai/ui';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { moneyInput, parseMoney } from '../wholesale/common';

/**
 * Cobro en el mostrador de un pedido mayorista que retiran en el local. La plata entra a esta caja, identificada como
 * cobro mayorista, pero no es una venta del mostrador: cuenta en el Mayorista.
 */
export function WholesaleCollectDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<WholesalePosOrderDTO | null>(null);
  const [method, setMethod] = useState<WholesalePaymentMethod>('CASH');
  const [amount, setAmount] = useState('');
  const list = useQuery({
    queryKey: ['wholesale', 'pos-orders', search],
    queryFn: () => api.get<WholesalePosOrderDTO[]>(`/wholesale/pos/orders${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });
  const save = useMutation({
    mutationFn: () => api.post<WholesalePosOrderDTO>('/wholesale/pos/collect', { orderId: picked!.id, method, amountCents: parseMoney(amount) ?? 0 }),
    onSuccess: async (o) => {
      await qc.invalidateQueries({ queryKey: ['wholesale'] });
      await qc.invalidateQueries({ queryKey: ['cash'] });
      toast.success(
        `Pedido mayorista #${o.number} cobrado`,
        o.dueCents > 0 ? `Queda ${formatMoney(o.dueCents)} en su cuenta corriente.` : 'Pagado completo.',
      );
      onClose();
    },
    onError: (e) => toast.error('No se pudo cobrar', errorMessage(e)),
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Cobrar pedido mayorista"
      description="Para los clientes mayoristas que retiran en el local. No es una venta del mostrador: cuenta en el Mayorista."
    >
      {!picked ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <Input
            placeholder="Buscar por cliente o número"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            aria-label="Buscar pedido"
          />
          {list.isLoading ? (
            <Skeleton style={{ height: 120 }} />
          ) : !list.data?.length ? (
            <p className="bf-cell-sub">No hay pedidos mayoristas para retirar con saldo.</p>
          ) : (
            list.data.map((o) => (
              <button
                key={o.id}
                type="button"
                className="bf-btn bf-btn--block"
                style={{ justifyContent: 'space-between' }}
                onClick={() => {
                  setPicked(o);
                  setAmount(moneyInput(o.dueCents));
                }}
              >
                <span>
                  #{o.number} · {o.customerName}
                </span>
                <strong>{formatMoney(o.dueCents)}</strong>
              </button>
            ))
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          <Callout tone="info">
            Pedido #{picked.number} · {picked.customerName}: total {formatMoney(picked.totalCents)}, falta {formatMoney(picked.dueCents)}.
          </Callout>
          <Field label="Medio">
            <Select value={method} onChange={(e) => setMethod(e.target.value as WholesalePaymentMethod)}>
              {WHOLESALE_PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {WHOLESALE_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Importe" hint="Si paga menos, el resto queda en su cuenta corriente.">
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <Button variant="ghost" onClick={() => setPicked(null)}>
              Otro pedido
            </Button>
            <Button variant="primary" loading={save.isPending} disabled={!parseMoney(amount)} onClick={() => save.mutate()}>
              Cobrar
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
